import { Button } from "primereact/button";
import React, { ErrorInfo, PropsWithChildren, ReactNode } from "react";

export interface GlobalErrorBoundaryProps {
  fallback?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
}

export class GlobalErrorBoundary extends React.Component<
  PropsWithChildren<GlobalErrorBoundaryProps>,
  GlobalErrorBoundaryState
> {
  constructor(props: PropsWithChildren<GlobalErrorBoundaryProps>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): GlobalErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Global Error Boundary Caught:", error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Example: send to monitoring service (Sentry, LogRocket, etc.)
    // Sentry.captureException(error, { extra: errorInfo });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex flex-column items-center justify-center h-screen bg-gray-100 text-center p-6">
            <h1 className="text-3xl font-bold mb-4">
              Etwas ist schiefgelaufen
            </h1>
            <p className="mb-6">
              Wir arbeiten daran, das Problem zu beheben. Bitte versuchen Sie es
              erneut.
            </p>
            <div>
              <Button
                onClick={this.handleReload}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                Seite neu laden
              </Button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
